(() => {
  if (window.KoppaCartConversion) return;
  const normalize = code => String(code || '').trim().toUpperCase();
  const labels = () => window.koppaConversionStrings || {};
  const report = detail => document.dispatchEvent(new CustomEvent('koppa:cart-result', {detail}));
  const request = async (path, body) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + path, {
        credentials: 'same-origin', signal: controller.signal,
        headers: {'Accept': 'application/json', ...(body === undefined ? {} : {'Content-Type': 'application/json'})},
        ...(body === undefined ? {} : {method: 'POST', body: JSON.stringify(body)})
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.status >= 400) throw new Error('cart_request_failed');
      return cart;
    } finally { clearTimeout(timeout); }
  };
  const savedCodes = cart => {
    const codes = (cart.discount_codes || []).map(entry => entry.code);
    const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application))];
    applications.forEach(entry => { const application = entry?.discount_application || entry; if (application?.type === 'discount_code') codes.push(application.title); });
    return [...new Set(codes.map(normalize).filter(Boolean))];
  };
  const savings = (cart, code) => {
    const matches = application => application?.type === 'discount_code' && normalize(application.title) === normalize(code);
    return (cart.cart_level_discount_applications || []).reduce((sum, entry) => { const application = entry?.discount_application || entry; return sum + (matches(application) ? Number(entry.amount ?? application.total_allocated_amount ?? 0) : 0); }, 0) +
      (cart.items || []).reduce((sum, item) => sum + (item.line_level_discount_allocations || []).reduce((total, allocation) => total + (matches(allocation.discount_application) ? Number(allocation.amount || 0) : 0), 0), 0);
  };
  const discountState = (cart, code) => {
    if (savings(cart, code) > 0) return 'applied';
    const entry = cart.discount_codes?.find(value => normalize(value.code) === normalize(code));
    if (!cart.item_count) return entry ? 'saved' : 'pending';
    if (entry?.applicable === false) {
      if (normalize(code) === 'GHK4U' && cart.items?.length && cart.items.every(item => item.selling_plan_allocation)) return 'subscription';
      return 'ineligible';
    }
    return 'pending';
  };
  const fingerprint = cart => JSON.stringify((cart.items || []).map(item => [item.variant_id || item.id, item.quantity, item.selling_plan_allocation?.selling_plan?.id || null, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]));
  let discountQueue = Promise.resolve();
  const updateDiscount = (code, options = {}) => {
    const run = async () => {
      const clean = normalize(code);
      const source = options.source === 'scratch' ? 'scratch' : 'cart';
      report({kind: 'discount', status: 'attempted', source});
      try {
        const before = await request('cart.js');
        const previous = savedCodes(before);
        if (!options.remove && options.preserveExisting && (Number(before.total_discount) > savings(before, clean) || previous.some(value => value !== clean))) {
          return {cart:before, state:'better', amount:savings(before, clean)};
        }
        const codes = options.remove ? previous.filter(value => value !== clean) : [...new Set([...previous, clean])];
        let cart = await request('cart/update.js', {discount: codes.join(',')});
        let state = options.remove ? 'removed' : discountState(cart, clean);
        if (!options.remove && fingerprint(before) === fingerprint(cart) && Number(before.total_discount) > Number(cart.total_discount)) {
          cart = await request('cart/update.js', {discount: previous.join(',')});
          state = 'better';
        } else if (!options.remove && ['ineligible', 'subscription'].includes(state)) {
          cart = await request('cart/update.js', {discount: previous.join(',')});
        }
        const amount = savings(cart, clean);
        report({kind: 'discount', status: state, source, savings: amount / 100, currency: cart.currency});
        return {cart, state, amount};
      } catch (error) {
        report({kind: 'discount', status: 'error', source, reason: 'cart_request_failed'});
        throw error;
      }
    };
    const pending = discountQueue.then(run, run);
    discountQueue = pending.catch(() => {});
    return pending;
  };
  const message = result => {
    const copy = labels();
    if (result.state === 'applied') {
      const amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: result.cart.currency || window.Shopify?.currency?.active || 'USD'}).format(result.amount / 100);
      return (copy.applied || '').replace('[amount]', amount);
    }
    return copy[result.state] || copy.pending;
  };
  const refresh = async () => {
    const page = document.querySelector('cart-items.cc-root');
    const drawer = document.querySelector('cart-drawer');
    if (page) await page.onCartUpdate();
    if (drawer?.classList.contains('active')) await drawer.refresh();
  };
  const preorder = product => Boolean(window.koppaStockState?.preorders?.[product.handle]);
  const item = product => {
    const line = {id: product.variantId, quantity: 1};
    if (preorder(product)) {
      const text = window.koppaStockState.labels;
      line.properties = {'Order type': text.preorder, 'Dispatch': text.dispatch, 'Fulfillment': text.fulfillment};
      if (product.handle === 'ghk-cu-trio') line.properties['Dispatch together'] = text.together;
    }
    return line;
  };
  const purchaseNote = products => {
    if (products.some(product => product.handle === 'ghk-cu-trio' && preorder(product))) return window.koppaStockState.labels.protocolNote;
    if (products.some(preorder)) return window.koppaStockState.labels.preorderNote;
    return labels().in_stock;
  };
  const readQuiz = () => {
    try {
      const context = JSON.parse(sessionStorage.getItem('koppaQuizCart') || 'null');
      if (!context || !Number.isFinite(context.expires) || context.expires <= Date.now() || context.expires > Date.now() + 1800000 || !Array.isArray(context.products) || !Array.isArray(context.goals)) return null;
      if (!context.products.every(product => product && typeof product.handle === 'string' && ['signal', 'refine', 'precision', 'trio'].includes(product.key)) || !context.goals.every(goal => typeof goal === 'string')) return null;
      return context;
    } catch (_) { return null; }
  };
  class KoppaQuizContext extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.abort?.abort();
      this.abort = new AbortController();
      document.addEventListener('koppa:quiz-context', () => this.render(), {signal: this.abort.signal});
      this.render();
    }
    render() {
      const context = readQuiz();
      let handles;
      try { handles = JSON.parse(this.dataset.handles || '[]'); } catch (_) { handles = []; }
      const products = Array.isArray(handles) ? context?.products.filter(product => handles.includes(product.handle)) || [] : [];
      this.hidden = !products.length;
      if (this.hidden) return;
      const copy = labels();
      const goals = context.goals.map(key => copy['goal_' + key]).filter(Boolean);
      const title = this.querySelector('[data-quiz-goals]');
      const list = this.querySelector('[data-quiz-routine]');
      if (!title || !list) { this.hidden = true; return; }
      title.textContent = goals.length ? copy.selected_for.replace('[goals]', goals.join(' · ')) : copy.your_routine;
      list.replaceChildren();
      products.forEach(product => {
        const text = copy['routine_' + product.key];
        if (!text) return;
        const line = document.createElement('li');
        line.textContent = text;
        list.append(line);
      });
      clearTimeout(this.expiry);
      this.expiry = setTimeout(() => this.render(), Math.max(1, context.expires - Date.now() + 1));
    }
    disconnectedCallback() { this.abort?.abort(); clearTimeout(this.expiry); }
  }
  if (!customElements.get('koppa-quiz-context')) customElements.define('koppa-quiz-context', KoppaQuizContext);
  class KoppaDrawerExpress extends HTMLElement {
    async connectedCallback() {
      this.hidden = true;
      if (document.querySelector('cart-items.cc-root')) return;
      const slot = this.querySelector('.additional-checkout-buttons');
      if (!slot) return;
      try {
        if (!slot.querySelector('shopify-accelerated-checkout-cart')) {
          this.controller = new AbortController();
          const timeout = setTimeout(() => this.controller.abort(), 12000);
          try {
            const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart?section_id=koppa-cart-wallets', {credentials:'same-origin',signal:this.controller.signal});
            if (!response.ok) return;
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const wallets = html.querySelector('[data-koppa-wallets]');
            if (!this.isConnected || !wallets?.querySelector('shopify-accelerated-checkout-cart')) return;
            slot.replaceChildren(...wallets.childNodes);
          } finally { clearTimeout(timeout); }
        }
        window.Shopify?.PaymentButton?.init();
        this.hidden = false;
      } catch (_) { this.hidden = true; }
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('koppa-drawer-express')) customElements.define('koppa-drawer-express', KoppaDrawerExpress);
  window.KoppaCartConversion = {updateDiscount, message, refresh, item, preorder, purchaseNote, savedCodes, savings, discountState, report};
})();
